export type EmbedBootStatus = 'resolving' | 'ready' | 'error';

export const embedBootStatus = (
  ref: string | null,
  hasActiveToken: boolean,
): EmbedBootStatus => {
  if (ref) return 'resolving';
  return hasActiveToken ? 'ready' : 'error';
};

export const isEmbedPath = (pathname?: string): boolean =>
  (
    pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '')
  ).startsWith('/embed');

export const EMBED_PREVIEW_PARAM = 'embedPreview';
export const EMBED_THEME_PARAM = 'theme';

export type EmbedTheme = 'dark' | 'light';

export const isEmbedPreviewSearch = (search: string): boolean => {
  try {
    return new URLSearchParams(search).has(EMBED_PREVIEW_PARAM);
  } catch {
    return false;
  }
};

export const getEmbedThemeParam = (search: string): EmbedTheme | null => {
  try {
    const t = new URLSearchParams(search).get(EMBED_THEME_PARAM);
    return t === 'dark' || t === 'light' ? t : null;
  } catch {
    return null;
  }
};

export const buildEmbedPreviewUrl = (
  origin: string,
  ref: string,
  theme?: EmbedTheme | null,
): string => {
  const base = `${origin}/embed/inbox?ref=${encodeURIComponent(ref)}&${EMBED_PREVIEW_PARAM}=1`;
  return theme === 'dark' || theme === 'light'
    ? `${base}&${EMBED_THEME_PARAM}=${theme}`
    : base;
};

export const embedReloadTarget = (pathname: string, search: string): string => {
  const params = new URLSearchParams();
  if (isEmbedPreviewSearch(search)) params.set(EMBED_PREVIEW_PARAM, '1');
  const theme = getEmbedThemeParam(search);
  if (theme) params.set(EMBED_THEME_PARAM, theme);
  const qs = params.toString();
  return qs ? `${pathname}?${qs}` : pathname;
};

export interface EmbedUiConfig {
  sessionExpiredText?: string;
  hideCallButton?: boolean;
  hideChatOptions?: boolean;
  showRefresh?: boolean;
}

export const EMBED_UI_KEY = 'embedUiConfig';

export const parseEmbedUi = (
  raw: string | null | undefined,
): EmbedUiConfig | null => {
  if (!raw) return null;
  let parsed: Partial<EmbedUiConfig>;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const ui: EmbedUiConfig = {};
  if (
    typeof parsed.sessionExpiredText === 'string' &&
    parsed.sessionExpiredText.trim().length > 0
  )
    ui.sessionExpiredText = parsed.sessionExpiredText;
  if (parsed.hideCallButton === true) ui.hideCallButton = true;
  if (parsed.hideChatOptions === true) ui.hideChatOptions = true;
  if (parsed.showRefresh === true) ui.showRefresh = true;
  return Object.keys(ui).length > 0 ? ui : null;
};

export const getEmbedUi = (): EmbedUiConfig | null =>
  typeof window === 'undefined'
    ? null
    : parseEmbedUi(window.sessionStorage.getItem(EMBED_UI_KEY));
